import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "../features/auth/AuthProvider";
import { WorkspaceProvider } from "../features/workspace/WorkspaceContext";
import { AppLayout } from "./layouts/AppLayout";
import { ErrorBoundary } from "./ErrorBoundary";
import { RequireAuth } from "./RequireAuth";
import LoginPage from "../pages/LoginPage";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import AcceptInvitePage from "../pages/AcceptInvitePage";
import LeadsPage from "../pages/LeadsPage";
import LeadDetailPage from "../pages/LeadDetailPage";
import TrashPage from "../pages/TrashPage";
import TeamPage from "../pages/TeamPage";
import MembersPage from "../pages/MembersPage";
import MemberDetailPage from "../pages/MemberDetailPage";
import DashboardPage from "../pages/DashboardPage";
import { SuperadminLayout } from "./layouts/SuperadminLayout";
import SuperadminWorkspacesPage from "../pages/superadmin/WorkspacesPage";
import SuperadminWorkspaceDetailPage from "../pages/superadmin/WorkspaceDetailPage";
import AddWorkspacePage from "../pages/superadmin/AddWorkspacePage";
import SuperadminToolsPage from "../pages/superadmin/ToolsPage";
import SuperadminFeatureFlagsPage from "../pages/superadmin/FeatureFlagsPage";
import SuperadminSystemTemplatesPage from "../pages/superadmin/SystemTemplatesPage";
import SuperadminDocsPage from "../pages/superadmin/DocsPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
    },
  },
});

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Toaster position="top-right" richColors closeButton />
        <BrowserRouter>
          <AuthProvider>
            <WorkspaceProvider>
              <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />

              <Route element={<RequireAuth />}>
                <Route element={<AppLayout />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/leads" element={<LeadsPage />} />
                  <Route path="/leads/:id" element={<LeadDetailPage />} />
                  <Route path="/leads/_trash" element={<TrashPage />} />
                  <Route path="/team" element={<TeamPage />} />
                  <Route path="/members" element={<MembersPage />} />
                  <Route path="/members/:userId" element={<MemberDetailPage />} />
                  <Route path="/superadmin" element={<SuperadminLayout />}>
                    <Route index element={<SuperadminWorkspacesPage />} />
                    <Route path="new" element={<AddWorkspacePage />} />
                    <Route path="tools" element={<SuperadminToolsPage />} />
                    <Route path="feature-flags" element={<SuperadminFeatureFlagsPage />} />
                    <Route path="system-templates" element={<SuperadminSystemTemplatesPage />} />
                    <Route path="docs" element={<SuperadminDocsPage />} />
                    <Route path=":id" element={<SuperadminWorkspaceDetailPage />} />
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </WorkspaceProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
